<template>
  <div class="groups-inventory-modern">
    <!-- Modals -->
    <NewGroup @created="getGroups" />
    <ViewGroup :group="group" @updated="getGroups" @close="group = null" />

    <!-- Main Content -->
    <div class="groups-content">
      <!-- Header Section -->
      <div class="content-header">
        <div class="header-left">
          <h2 class="section-title">
            <i class="fas fa-warehouse section-icon"></i>
            Grupos
          </h2>
          <p class="section-subtitle">
            Gerencie locais de armazenamento e organização do estoque
          </p>
        </div>
        <div class="header-actions">
          <v-btn
            v-if="permission('write', 'stock')"
            @click="$bvModal.show('new_group')"
            class="btn-add-modern"
          >
            <i class="fas fa-plus mr-2"></i>
            Adicionar Grupo
          </v-btn>
        </div>
      </div>

      <!-- Filters Section -->
      <div class="filters-section">
        <div class="filters-card">
          <div class="filter-group">
            <label class="filter-label">
              <i class="fas fa-search filter-icon"></i>
              Pesquisar
            </label>
            <b-input
              v-model="search"
              placeholder="Digite o nome do grupo"
              class="modern-input"
            />
          </div>
        </div>
      </div>

      <!-- Content Grid -->
      <div class="content-grid">
        <!-- Table Section -->
        <div class="table-section">
          <!-- No Results -->
          <div v-show="stock_groups.length == 0" class="no-results">
            <i class="fas fa-search no-results-icon"></i>
            <h3 class="no-results-title">Nenhum grupo encontrado</h3>
            <p class="no-results-text">
              Tente ajustar os filtros ou adicione um novo grupo
            </p>
          </div>

          <!-- Data Table -->
          <div v-show="stock_groups.length > 0" class="table-card">
            <v-data-table-default
              :search="search"
              :headers="headers"
              :items="stock_groups"
              class="modern-table"
            >
              <template #[`item.name`]="{ item }">
                <div class="group-name-cell">
                  <div class="group-name">{{ item.name }}</div>
                  <small v-if="item.main_stock" class="main-stock-indicator">
                    (APENAS ESTE ESTOQUE É UTILIZADO NA CHECAGEM DE
                    MEDICAMENTOS)
                  </small>
                </div>
              </template>

              <template v-slot:[`item.actions`]="{ item }">
                <div class="actions-cell">
                  <v-btn
                    v-if="permission('edit', 'stock')"
                    small
                    @click="group = item"
                    class="btn-action-edit"
                  >
                    <i class="fas fa-edit mr-1"></i>
                    Editar
                  </v-btn>

                  <confirmation-dialog
                    small
                    message-title="EXCLUIR GRUPO"
                    message-body="Deseja realmente excluir o grupo de estoque? Esta ação não pode ser desfeita."
                    open-class="btn-action-delete"
                    open-left-icon="fa-trash"
                    open-text="Excluir"
                    @yes="deleteGroup(item)"
                  />
                </div>
              </template>
            </v-data-table-default>
          </div>
        </div>

        <!-- Info Section -->
        <div class="info-section">
          <div class="info-card">
            <div class="info-header">
              <h3 class="info-title">
                <i class="fas fa-info-circle mr-2"></i>
                Definição de Grupos
              </h3>
            </div>
            <div class="info-content">
              <div class="info-item">
                <h4 class="info-subtitle">O que são grupos?</h4>
                <p class="info-text">
                  Locais onde ficam armazenados os estoques de produtos e
                  medicamentos, facilitando a organização e controle do
                  inventário.
                </p>
              </div>

              <div class="info-item">
                <h4 class="info-subtitle">Exemplos de grupos:</h4>
                <div class="examples-grid">
                  <div class="example-item">
                    <i class="fas fa-user-nurse example-icon"></i>
                    <span>Posto de Enfermagem</span>
                  </div>
                  <div class="example-item">
                    <i class="fas fa-warehouse example-icon"></i>
                    <span>Dispensa</span>
                  </div>
                  <div class="example-item">
                    <i class="fas fa-boxes example-icon"></i>
                    <span>Almoxarifado</span>
                  </div>
                  <div class="example-item">
                    <i class="fas fa-utensils example-icon"></i>
                    <span>Cozinha</span>
                  </div>
                  <div class="example-item">
                    <i class="fas fa-pills example-icon"></i>
                    <span>Farmácia</span>
                  </div>
                  <div class="example-item">
                    <i class="fas fa-clinic-medical example-icon"></i>
                    <span>Consultório</span>
                  </div>
                </div>
              </div>

              <div class="info-item">
                <h4 class="info-subtitle">Próximos passos:</h4>
                <div class="steps-list">
                  <div class="step-item">
                    <div class="step-number">1</div>
                    <div class="step-content">
                      <strong>Cadastre o grupo</strong>
                      <span>Defina o nome do local de armazenamento</span>
                    </div>
                  </div>
                  <div class="step-item">
                    <div class="step-number">2</div>
                    <div class="step-content">
                      <strong>Acesse a aba Categorias</strong>
                      <span>Configure as categorias de produtos</span>
                    </div>
                  </div>
                  <div class="step-item">
                    <div class="step-number">3</div>
                    <div class="step-content">
                      <strong>Associe categorias ao grupo</strong>
                      <span>Vincule categorias aos locais apropriados</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Error Messages -->
      <ErrorsMessages :errors="errors" v-if="errors.length > 0" />
    </div>
  </div>
</template>

<script>
import ErrorsMessages from "../../Components/ErrorsMessages.vue";
import VDataTableDefault from "../../Components/VDataTableDefault.vue";

import NewGroup from "./new-group.vue";
import ViewGroup from "./view-group.vue";

import api from "../../../services/api";

import permission from "../../../lib/permission";

import ConfirmationDialog from "../../Components/ConfirmationDialog";

import toast from "../../../lib/ToastNotification";

export default {
  name: "Groups",

  components: {
    NewGroup,
    ViewGroup,
    ErrorsMessages,
    VDataTableDefault,
    ConfirmationDialog,
  },

  data() {
    return {
      errors: [],
      loading: false,

      search: "",
      headers: [
        { text: "NOME", value: "name" },
        { text: "AÇÕES", value: "actions", width: "auto", sortable: false },
      ],
      stock_groups: [],

      group: null,
    };
  },

  created() {
    this.getGroups();
  },

  methods: {
    permission,

    async getGroups() {
      try {
        this.loading = true;

        const response = await api.get("/rest-home/stock/groups");
        const { error, errors, stock_groups } = response.data;

        if (error) {
          this.errors = errors;
        } else {
          this.errors = [];
          this.stock_groups = stock_groups;
        }
      } catch (error) {
        console.error(error);
      } finally {
        this.loading = false;
      }
    },

    async deleteGroup(group) {
      try {
        this.loading = true;

        const data = {
          id: group.id,
        };

        const response = await api.delete("/rest-home/stock/group", { data });

        const { error, errors, message } = response.data;

        if (error) {
          this.errors = errors;
        } else {
          this.errors = [];

          toast(message, "success");

          this.getGroups();
        }
      } catch (error) {
        console.error(error);
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style scoped>
.groups-inventory-modern {
  background: transparent;
}

/* Content Section */
.groups-content {
  display: flex;
  flex-direction: column;
  gap: 24px;
}

/* Header Section */
.content-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 8px;
  gap: 24px;
}

.header-left {
  flex: 1;
}

.section-title {
  font-size: 24px;
  font-weight: 600;
  color: #1e293b;
  margin: 0 0 4px 0;
  display: flex;
  align-items: center;
}

.section-icon {
  color: #0f65b0;
  margin-right: 12px;
  font-size: 20px;
}

.section-subtitle {
  color: #64748b;
  margin: 0;
  font-size: 14px;
}

.header-actions {
  display: flex;
  gap: 12px;
}

.btn-add-modern {
  background: linear-gradient(135deg, #4e9130 0%, #1e884d 100%) !important;
  color: white !important;
  border: none !important;
  border-radius: 12px !important;
  padding: 12px 24px !important;
  font-weight: 600 !important;
  text-transform: none !important;
  box-shadow: 0 4px 15px rgba(78, 145, 48, 0.3) !important;
  transition: all 0.3s ease !important;
}

.btn-add-modern:hover {
  transform: translateY(-2px) !important;
  box-shadow: 0 6px 20px rgba(78, 145, 48, 0.4) !important;
}

/* Filters Section */
.filters-section {
  margin-bottom: 8px;
}

.filters-card {
  background: white;
  border-radius: 16px;
  padding: 24px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  border: 1px solid rgba(255, 255, 255, 0.2);
}

.filter-group {
  display: flex;
  flex-direction: column;
  gap: 8px;
  max-width: 400px;
}

.filter-label {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  font-weight: 600;
  color: #374151;
  margin: 0;
}

.filter-icon {
  color: #6b7280;
  font-size: 14px;
}

.modern-input {
  border: 2px solid #e5e7eb !important;
  border-radius: 8px !important;
  font-size: 14px !important;
  transition: all 0.3s ease !important;
  background: white !important;
}

.modern-input:focus {
  border-color: #0f65b0 !important;
  box-shadow: 0 0 0 3px rgba(15, 101, 176, 0.1) !important;
  outline: none !important;
}

/* Content Grid */
.content-grid {
  display: grid;
  grid-template-columns: 1fr 400px;
  gap: 32px;
  align-items: start;
}

/* Table Section */
.table-section {
  width: 100%;
}

.no-results {
  background: white;
  border-radius: 16px;
  padding: 60px 24px;
  text-align: center;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  border: 1px solid rgba(255, 255, 255, 0.2);
}

.no-results-icon {
  font-size: 64px;
  color: #cbd5e1;
  margin-bottom: 20px;
}

.no-results-title {
  color: #1e293b;
  font-size: 20px;
  font-weight: 600;
  margin: 0 0 8px 0;
}

.no-results-text {
  color: #64748b;
  font-size: 16px;
  margin: 0;
}

.table-card {
  background: white;
  border-radius: 16px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  border: 1px solid rgba(255, 255, 255, 0.2);
  overflow: hidden;
}

/* Table Cell Styling */
.group-name-cell {
  padding: 8px 0;
}

.group-name {
  font-weight: 500;
  color: #1e293b;
  font-size: 14px;
  margin-bottom: 4px;
}

.main-stock-indicator {
  display: block;
  color: #0f65b0;
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.actions-cell {
  display: flex;
  gap: 8px;
  align-items: center;
}

.btn-action-edit {
  background: linear-gradient(135deg, #1d75be 0%, #155a8a 100%) !important;
  color: white !important;
  border: none !important;
  border-radius: 8px !important;
  font-weight: 600 !important;
  text-transform: none !important;
  font-size: 11px !important;
  box-shadow: 0 2px 8px rgba(29, 117, 190, 0.3) !important;
  transition: all 0.3s ease !important;
}

.btn-action-edit:hover {
  transform: translateY(-1px) !important;
  box-shadow: 0 4px 12px rgba(29, 117, 190, 0.4) !important;
}

.btn-action-delete {
  background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%) !important;
  color: white !important;
  border: none !important;
  border-radius: 8px !important;
  font-weight: 600 !important;
  text-transform: none !important;
  font-size: 11px !important;
  box-shadow: 0 2px 8px rgba(239, 68, 68, 0.3) !important;
  transition: all 0.3s ease !important;
  padding: 6px 12px !important;
}

.btn-action-delete:hover {
  transform: translateY(-1px) !important;
  box-shadow: 0 4px 12px rgba(239, 68, 68, 0.4) !important;
}

/* Info Section */
.info-section {
  position: sticky;
  top: 24px;
}

.info-card {
  background: white;
  border-radius: 16px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  border: 1px solid rgba(255, 255, 255, 0.2);
  overflow: hidden;
}

.info-header {
  background: linear-gradient(135deg, #e0f2fe 0%, #b3e5fc 100%);
  padding: 20px;
  border-bottom: 1px solid #0284c7;
}

.info-title {
  font-size: 16px;
  font-weight: 600;
  color: #0c4a6e;
  margin: 0;
  display: flex;
  align-items: center;
}

.info-title i {
  color: #0284c7;
}

.info-content {
  padding: 24px;
}

.info-item {
  margin-bottom: 24px;
}

.info-item:last-child {
  margin-bottom: 0;
}

.info-subtitle {
  font-size: 14px;
  font-weight: 600;
  color: #1e293b;
  margin: 0 0 12px 0;
}

.info-text {
  font-size: 13px;
  color: #64748b;
  line-height: 1.6;
  margin: 0;
}

.examples-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
}

.example-item {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  background: #f8fafc;
  border-radius: 8px;
  border: 1px solid #e5e7eb;
  font-size: 12px;
  color: #374151;
}

.example-icon {
  color: #0f65b0;
  font-size: 14px;
  width: 16px;
}

.steps-list {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.step-item {
  display: flex;
  align-items: flex-start;
  gap: 12px;
}

.step-number {
  width: 24px;
  height: 24px;
  background: linear-gradient(135deg, #0f65b0 0%, #1e40af 100%);
  color: white;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: 600;
  flex-shrink: 0;
}

.step-content {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.step-content strong {
  font-size: 13px;
  color: #1e293b;
  font-weight: 600;
}

.step-content span {
  font-size: 12px;
  color: #64748b;
}

/* Responsive Design */
@media (max-width: 1200px) {
  .content-grid {
    grid-template-columns: 1fr;
    gap: 24px;
  }

  .info-section {
    position: static;
  }
}

@media (max-width: 1024px) {
  .content-header {
    flex-direction: column;
    align-items: stretch;
    gap: 16px;
  }
}

@media (max-width: 768px) {
  .filters-card {
    padding: 16px;
  }

  .section-title {
    font-size: 20px;
  }

  .actions-cell {
    flex-direction: column;
    gap: 4px;
  }

  .btn-action-edit,
  .btn-action-delete {
    width: 100% !important;
    justify-content: center !important;
  }

  .examples-grid {
    grid-template-columns: 1fr;
    gap: 8px;
  }

  .info-content {
    padding: 16px;
  }
}

@media (max-width: 480px) {
  .section-title {
    font-size: 18px;
    flex-direction: column;
    gap: 4px;
  }

  .section-subtitle {
    font-size: 13px;
  }

  .info-header {
    padding: 16px;
  }

  .info-title {
    font-size: 14px;
  }
}

/* Override Vuetify styles */
.modern-table >>> .v-data-table__wrapper {
  border-radius: 0 !important;
}

.modern-table >>> .v-data-table thead th {
  background-color: #f8fafc !important;
  color: #374151 !important;
  font-weight: 600 !important;
  font-size: 12px !important;
  text-transform: uppercase !important;
  letter-spacing: 0.5px !important;
  border-bottom: 2px solid #e5e7eb !important;
}

.modern-table >>> .v-data-table tbody tr:hover {
  background-color: #f8fafc !important;
}

/* Enhanced hover effects */
.table-card:hover {
  box-shadow: 0 6px 25px rgba(0, 0, 0, 0.12) !important;
  transition: box-shadow 0.3s ease !important;
}

.info-card:hover {
  box-shadow: 0 6px 25px rgba(0, 0, 0, 0.12) !important;
  transition: box-shadow 0.3s ease !important;
}
</style>